import * as mediasoupClient from 'mediasoup-client';
import { getSocket, emitAsync } from './socket';

let device = null;
let sendTransport = null;
let recvTransport = null;
let producers = new Map();
let consumers = new Map();
let recvTransportPromise = null; // Mutex for recv transport creation

/**
 * Get video encodings based on resolution
 * Adaptive bitrate for different quality levels
 */
function getVideoEncodings(width, height) {
  if (width >= 3840) {
    // 4K
    return [
      { maxBitrate: 500000, scaleResolutionDownBy: 4 }, // ~960x540
      { maxBitrate: 1500000, scaleResolutionDownBy: 2 }, // ~1920x1080
      { maxBitrate: 5000000 }, // Full 4K - 5 Mbps
    ];
  } else if (width >= 2560) {
    // 1440p
    return [
      { maxBitrate: 400000, scaleResolutionDownBy: 4 },
      { maxBitrate: 1200000, scaleResolutionDownBy: 2 },
      { maxBitrate: 3500000 }, // 3.5 Mbps
    ];
  } else if (width >= 1920) {
    // 1080p
    return [
      { maxBitrate: 300000, scaleResolutionDownBy: 4 },
      { maxBitrate: 900000, scaleResolutionDownBy: 2 },
      { maxBitrate: 2500000 }, // 2.5 Mbps
    ];
  } else if (width >= 1280) {
    // 720p
    return [
      { maxBitrate: 150000, scaleResolutionDownBy: 4 },
      { maxBitrate: 500000, scaleResolutionDownBy: 2 },
      { maxBitrate: 1500000 }, // 1.5 Mbps
    ];
  } else {
    // 480p and below
    return [
      { maxBitrate: 100000, scaleResolutionDownBy: 4 },
      { maxBitrate: 300000, scaleResolutionDownBy: 2 },
      { maxBitrate: 900000 }, // 900 Kbps
    ];
  }
}

export async function loadDevice() {
  if (device) return device;

  device = new mediasoupClient.Device();

  const { rtpCapabilities } = await emitAsync('get-router-capabilities');
  await device.load({ routerRtpCapabilities: rtpCapabilities });

  // Send RTP capabilities to server
  const socket = getSocket();
  socket.emit('set-rtp-capabilities', { rtpCapabilities: device.rtpCapabilities });

  return device;
}

export async function createSendTransport() {
  const { transport } = await emitAsync('create-transport', { direction: 'send' });

  sendTransport = device.createSendTransport(transport);

  sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
    try {
      await emitAsync('connect-transport', {
        transportId: sendTransport.id,
        dtlsParameters,
      });
      callback();
    } catch (error) {
      errback(error);
    }
  });

  sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
    try {
      const { producerId } = await emitAsync('produce', {
        transportId: sendTransport.id,
        kind,
        rtpParameters,
        appData,
      });
      callback({ id: producerId });
    } catch (error) {
      errback(error);
    }
  });

  return sendTransport;
}

export async function createRecvTransport() {
  const { transport } = await emitAsync('create-transport', { direction: 'recv' });

  recvTransport = device.createRecvTransport(transport);

  recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
    try {
      await emitAsync('connect-transport', {
        transportId: recvTransport.id,
        dtlsParameters,
      });
      callback();
    } catch (error) {
      errback(error);
    }
  });

  return recvTransport;
}

export async function produce(track, appData = {}) {
  if (!sendTransport) {
    await createSendTransport();
  }

  // Get video track settings for adaptive bitrate
  let videoEncodings = null;
  if (track.kind === 'video') {
    const settings = track.getSettings();
    const width = settings.width || 1280;
    const height = settings.height || 720;
    videoEncodings = getVideoEncodings(width, height);
    console.log(`[Mediasoup] Video track: ${width}x${height}, encodings:`, videoEncodings);
  }

  const producer = await sendTransport.produce({
    track,
    appData,
    // Audio settings
    ...(track.kind === 'audio' && {
      codecOptions: {
        opusStereo: true,
        opusDtx: true,
      },
    }),
    // Video settings with adaptive bitrate
    ...(track.kind === 'video' && {
      encodings: videoEncodings,
      codecOptions: {
        videoGoogleStartBitrate: 1000,
      },
    }),
  });

  producers.set(producer.id, producer);

  producer.on('transportclose', () => {
    console.log(`[Mediasoup] Producer ${producer.id} (${producer.kind}) - transport closed`);
    producers.delete(producer.id);
  });

  producer.on('trackended', () => {
    console.log(`[Mediasoup] Producer ${producer.id} (${producer.kind}) - track ended! This will close the producer.`);
    closeProducer(producer.id);
  });

  console.log(`[Mediasoup] Producer created: ${producer.id} (${producer.kind})`);

  return producer;
}

export async function consume(producerId) {
  // Use mutex to prevent race condition when creating recv transport
  if (!recvTransport) {
    if (!recvTransportPromise) {
      recvTransportPromise = createRecvTransport();
    }
    await recvTransportPromise;
  }

  const { consumer } = await emitAsync('consume', {
    transportId: recvTransport.id,
    producerId,
  });

  const consumerObj = await recvTransport.consume({
    id: consumer.id,
    producerId: consumer.producerId,
    kind: consumer.kind,
    rtpParameters: consumer.rtpParameters,
    appData: consumer.appData,
  });

  consumers.set(consumerObj.id, consumerObj);

  // Resume consumer
  await emitAsync('resume-consumer', { consumerId: consumerObj.id });

  return consumerObj;
}

export function closeProducer(producerId) {
  const producer = producers.get(producerId);
  if (producer) {
    producer.close();
    producers.delete(producerId);
    const socket = getSocket();
    socket.emit('close-producer', { producerId });
  }
}

export function pauseProducer(producerId) {
  const producer = producers.get(producerId);
  if (producer) {
    producer.pause();
    const socket = getSocket();
    socket.emit('pause-producer', { producerId });
  }
}

export function resumeProducer(producerId) {
  const producer = producers.get(producerId);
  if (producer) {
    producer.resume();
    const socket = getSocket();
    socket.emit('resume-producer', { producerId });
  }
}

export function getProducer(kind) {
  for (const producer of producers.values()) {
    if (producer.kind === kind && !producer.appData.isScreenShare) {
      return producer;
    }
  }
  return null;
}

export function getScreenShareProducer() {
  for (const producer of producers.values()) {
    if (producer.appData.isScreenShare) {
      return producer;
    }
  }
  return null;
}

/**
 * Replace the video track on an existing producer
 * Used when switching video effects on/off
 */
export async function replaceVideoTrack(newTrack) {
  const videoProducer = getProducer('video');
  if (!videoProducer) {
    console.warn('[Mediasoup] No video producer to replace track');
    return false;
  }

  try {
    await videoProducer.replaceTrack({ track: newTrack });
    console.log('[Mediasoup] Video track replaced successfully');
    return true;
  } catch (error) {
    console.error('[Mediasoup] Failed to replace video track:', error);
    return false;
  }
}

export function closeAllConsumersForPeer(peerId) {
  for (const [id, consumer] of consumers) {
    if (consumer.appData.peerId === peerId) {
      consumer.close();
      consumers.delete(id);
    }
  }
}

export function cleanup() {
  for (const producer of producers.values()) {
    producer.close();
  }
  producers.clear();

  for (const consumer of consumers.values()) {
    consumer.close();
  }
  consumers.clear();

  if (sendTransport) {
    sendTransport.close();
    sendTransport = null;
  }

  if (recvTransport) {
    recvTransport.close();
    recvTransport = null;
  }

  recvTransportPromise = null;
  device = null;
}

export { device, sendTransport, recvTransport, producers, consumers };
